'use client';

import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

// UI components & helpers from your codebase:
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { PhoneInputField } from '@eliadmin/core/src/components/ui/phone-input/phone-input';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { uploadFile } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRouter } from 'next/navigation';

import { useMutation } from '@tanstack/react-query';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';

// 1) Define the Zod schema for your form fields
const customDesignFormSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    file: z.any().optional(), // or more specific if needed
    text: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

type CustomDesignFormValues = z.infer<ReturnType<typeof customDesignFormSchema>>;

// 2) Create your form component
export function CustomDesignForm() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<CustomDesignFormValues>({
    resolver: zodResolver(customDesignFormSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      file: null,
      text: '',
      gdpr: false,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const { toast } = useToast();

  // 3) Handle form submission (just console.log the data)
  const onSubmit = form.handleSubmit(async data => {
    try {

      if (data.file) {
        const formData = new FormData();

        formData.append('file', data.file);

        const res = await uploadFile({
          formData,
          locale,
        });
        data.file = res.data as unknown as File;
      }

      const customDesignData = {
        subject: 'Mám vlastný vzor',
        name: data.name,
        email: data.email,
        phone: data.phone,
        attachments: data.file ? [data.file] : [],
        text: data.text,
        gdpr: data.gdpr,
      };

      await mutateAsync({
        data: {
          ...customDesignData,

        } as any,
        locale,
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <div className="flex w-full justify-center">
      <Form {...form}>
        <form
          onSubmit={onSubmit}
          className={cn(
            'my-6 flex w-full max-w-[700px] flex-col items-start space-y-3 rounded-md border-border sm:border sm:px-4',
            'ea-custom-design-form',
          )}
        >
          {/* HEADER */}
          <div
            className={cn('mt-2 w-full max-w-[412px] px-2 py-2', 'ea-custom-design-form__header')}
          >
            <h1
              data-ea-visual-tag="h3"
              className={cn(
                'mb-4 text-start text-3xl font-bold',
                'ea-custom-design-form__header-title',
              )}
            >
              {t('text2035')}
            </h1>
            <p
              className={cn('text-starat mt-2 font-semibold', 'ea-custom-design-form__description')}
            >
              {t('text2036')}
            </p>
          </div>

          {/* CONTENT */}
          <div
            className={cn(
              'flex w-full flex-col space-y-1 sm:w-10/12',
              'ea-custom-design-form__content',
            )}
          >
            {/* Vaše meno */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem required className={cn('', 'ea-custom-design-form__name-field')}>
                  <FormLabel>{t('text1250')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('text1250')}
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* E-mail adresa */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem required className={cn('', 'ea-custom-design-form__email-field')}>
                  <FormLabel>{t('text35')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="E-mail"
                      className={{ wrapper: 'rounded-lg', main: 'rounded-lg' }}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Telefón */}
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem required className={cn('', 'ea-custom-design-form__phone-field')}>
                  <FormLabel>{t('text36')}</FormLabel>
                  <FormControl>
                    <PhoneInputField {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Upload (file input) */}
            <FormField
              control={form.control}
              name="file"
              render={({ field }) => (
                <FormItem className="ea-custom-design-form__file-field space-y-2">
                  {/* Label */}
                  <FormLabel className="block font-medium capitalize text-gray-700">
                    {t('text1285').toLowerCase()}
                  </FormLabel>
                  <FormControl>
                    {/* Wrapper to style like an input field */}
                    <div className="relative flex items-center rounded-lg border border-gray-300 bg-gray-50 px-4 py-2">
                      {/* Hidden native file input */}
                      <input
                        type="file"
                        id="file-upload"
                        onChange={e => field.onChange(e.target.files?.[0] ?? null)}
                        style={{ display: 'none' }}
                      />

                      {/* Custom button that triggers the file input */}
                      <button
                        type="button"
                        onClick={() => document.getElementById('file-upload')?.click()}
                        className="rounded-md bg-white px-4 py-2 font-medium text-gray-700 hover:bg-gray-100"
                      >
                        {t('text950')}
                      </button>

                      {/* Display the file name if a file is selected */}
                      {field.value && (
                        <span className="ml-3 text-sm text-gray-700">{field.value.name}</span>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Správa (textarea) */}
            <FormField
              control={form.control}
              name="text"
              render={({ field }) => (
                <FormItem className={cn('', 'ea-custom-design-form__message-field')}>
                  <FormLabel>{t('text80')}</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder={t('text2340')}
                      className={cn(
                        'w-full rounded-lg border border-input px-3 py-2',
                        'focus:outline-none focus:ring-1 focus:ring-primary',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex items-start',
                  'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                  />
                </FormControl>
                <FormLabel className={cn('pt-[3px]', 'ea-contact-form__gdpr-checkbox-label')}>
                  <GDPRTextContactForm locale={locale} />
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('col-span-12')}>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* FOOTER */}
          <div
            className={cn(
              'flex w-full flex-col items-end space-y-4 pb-12',
              'ea-custom-design-form__footer',
            )}
          >
            <Button
              type="submit"
              className={cn(
                'h-12 w-full text-lg sm:w-auto',
                'ea-custom-design-form__submit-button',
              )}
              isLoading={form.formState.isSubmitting}
            >
              {t('text185')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
